import { useCallback, useEffect, useRef, useState } from 'react';

export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

export function useApiQuery<T>(loader: () => Promise<T>): QueryState<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loaderRef.current());
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error('Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload };
}

export function useMutation<TArgs extends unknown[], TResult>(action: (...args: TArgs) => Promise<TResult>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const run = useCallback(async (...args: TArgs) => {
    setBusy(true);
    setError(null);
    try {
      return await action(...args);
    } catch (caught) {
      const normalized = caught instanceof Error ? caught : new Error('Error desconocido');
      setError(normalized);
      throw normalized;
    } finally {
      setBusy(false);
    }
  }, [action]);

  return { run, busy, error, clearError: () => setError(null) };
}
